/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        Sparta Input ADA Utility
 * @file        sparta-input-ada-utility.js
 * @description Provides aria-describedby messaging for input types
 *   Developer Note: Error ADA update are done by sparta-input-error-utility ^7.1.1 or greater
 * @global      Creates a singleton of the global spartaInputAdaUtility class
 * @author      Gregory Thomason
 * @copyright   © 2020 Bank of America Corporation. All rights reserved.
 *
 * The Sparta Input ADA Utility is used to update input aria-describedby attribute so it proper
 * reads out information to a screen reader user.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/**
 * @class SpartaInputAdaUtility
 * @classdesc Class for global implementation of the standard Sparta ADA messaging
 */
class SpartaInputAdaUtility {
  // initialize utility
  /**
   * Init function for utility
   * @param {*} fields - input fields to test against
   */
  init(fields) {
    // create variable to reach scoped functions
    const self = this;

    // loop through each field and set up ADA controls
    fields.forEach(element => {
      let _field = element;
      const _fieldType = _field.classList.contains('spa-input-box') ? 'inputSingle' : 'checkboxGroup';
      const _wrapper = _fieldType === 'inputSingle' ? _field : _field.parentNode;

      // get input type if we're not in a checkbox/radio group
      if (_fieldType === 'inputSingle') {
        _field = _field.querySelector('input, select, textarea');
      }

      // universal requirements
      _wrapper.querySelector('label') || null;
      // execute the function after the page loads
      window.onload = () => {
        const _inputID = _field.getAttribute('id') || null;

        // get element and ID strings
        _wrapper.querySelector(`#help-open-${_inputID}`) || null;
        _wrapper.querySelector(`#helpLayer_${_inputID}`) || null;
        const hintText = _wrapper.querySelector(`#${_inputID}-hint-text`) || null;
        const hintTextString = hintText ? `${_inputID}-hint-text ` : '';
        const error = _wrapper.querySelector(`#${_inputID}_errorMessage`) || null;
        const errorString = error ? `${_inputID}_errorMessage ` : '';
        let groupHintTextString = '';
        let groupErrorString = '';
        if (_fieldType === 'checkboxGroup') {
          // check if checkbox has siblings (vertical or horizontal grouping)
          let groupSiblings = 0;
          if (self._getClosest(_wrapper, '.spa-input-options-list')) {
            groupSiblings = self._getClosest(_wrapper, '.spa-input-options-list').children.length;
          }

          // if not part of a group than skip rest of iteration checks
          if (groupSiblings <= 1) return;

          // get group requirements
          const groupWrapper = self._getClosest(_wrapper, 'fieldset');
          const groupID = groupWrapper.getAttribute('id') || null;

          // get group elements and ID strings
          groupWrapper.querySelector(`#help-open-${groupID}`);
          groupWrapper.querySelector(`#helpLayer_${groupID}`);
          const groupHintText = groupWrapper.querySelector(`#${groupID}-hint-text`);
          groupHintTextString = groupHintText ? `${groupID}-hint-text ` : '';
          const groupError = groupWrapper.querySelector(`#${groupID}_errorMessage`);
          groupErrorString = groupError ? `${groupID}_errorMessage ` : '';
        }

        // create aria-describedby string
        // developer note: invalid updates to this string are done by sparta-input-error-utility ^7.1.1 or greater
        const setHintString = hintTextString + (groupHintTextString && groupHintTextString.length ? groupHintTextString : '');
        const setErrorString = errorString + (groupErrorString && groupErrorString.length ? groupErrorString : '');
        const string = `${setErrorString} ${setHintString}`;

        // append ada string to input element
        if (string.length >= 1) {
          _field.setAttribute('aria-describedby', string);
        }
      };
    }); // END loop
  } // END init() function

  /**
   * vanilla form of jquery .closeset
   * @param {*} elem - element to test against.
   * @param {*} selector - selector to find.
   */
  _getClosest(elem, selector) {
    // Element.matches() polyfill
    if (!Element.prototype.matches) {
      Element.prototype.matches = Element.prototype.matchesSelector || Element.prototype.mozMatchesSelector || Element.prototype.msMatchesSelector || Element.prototype.oMatchesSelector || Element.prototype.webkitMatchesSelector ||
      // eslint-disable-next-line func-names
      function (s) {
        const matches = document.querySelectorAll(s);
        let i = matches.length;
        while (--i >= 0 && matches.item(i) !== this) {}
        return i > -1;
      };
    }

    // Get closest match
    for (; elem && elem !== document; elem = elem.parentNode) {
      if (elem.matches(selector)) return elem;
    }
    return null;
  }
} // END SpartaInputAdaUtility

// export new instance of the input ada messaging utility
var index = new SpartaInputAdaUtility();

export { index as default };
